import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { Color, FontSize, CommonProperties, CommonStyles } from '../../styles';
import Images from '../../constants/Images';
import Icon from '../../components/Icon';
import { formatCurrency } from '../../utils/currency.utils';
import { timeConvert24toAMPM, timeConvertObjectToDuration } from '../../utils/time.utils';
import { getSegmentsCountText } from '../../utils/segmentsCount.util';
import { useDispatch } from 'react-redux';
import { selectRail } from '../../store/actions/rail';
import Card from './Card';
import { CardLeftTextDetail, ShrinkView, cardStyle, XSmallText } from './card.utils';
import PriceItem from './PriceItem';
import RowView from '../RowView';

const transformRailBrands = (brands, currency, currencyCulture) => {
  if (!brands) {
    return [];
  }

  return brands
    .sort((a, b) => {
      return a.totalPriceToDisplay - b.totalPriceToDisplay;
    })
    .map((brand) => {
      const { segBrandNames, brandName, multiClassFares } = brand;
      return multiClassFares.map((classFare, idx) => {
        const { amount, fareCodes, fareName, offerId } = classFare;
        const name = brandName === fareName ? brandName : `${brandName} - ${fareName}`;
        return {
          id: `${brandName}-${idx}`,
          offerId,
          classCode: fareCodes.join(','),
          segBrandNames: segBrandNames[0].replace(' Class Seat', ''),
          name,
          price: formatCurrency(amount, currency, currencyCulture),
        };
      });
    })
    .flat();
};

const generateDefaultBrand = (item) => [
  {
    id: item.Id,
    name: item.BrandName,
    classCode: item.ClassCode,
    segBrandNames: item.SegmentBrandName.replace(' Class Seat', ''),
  },
];

const getRailSegmentsText = (list = []) => {
  if (Array.isArray(list)) {
    return list
      .reduce((acum, curr) => {
        if (curr) {
          acum.push(curr.departureLocationCode || curr.DepartureLocationCode);
          acum.push(curr.arrivalLocationCode || curr.ArrivalLocationCode);
        }
        return acum;
      }, [])
      .filter((value, index, array) => value !== array[index + 1])
      .join(' - ');
  }

  return ' ';
};

const transformRailDataToCard = (item = {}, currentBrand, currency, currencyCulture) => {
  return {
    title:
      timeConvert24toAMPM(item.departureTime || item.DepartTime) +
      ' - ' +
      timeConvert24toAMPM(item.arrivalTime || item.ArrivalTime),
    subtitle: getRailSegmentsText(item.railSegments || item.RailSegments),
    value:
      currentBrand?.toLowerCase() == 'business'
        ? item.multiBrandFare && item.multiBrandFare[1].totalPriceToDisplay
          ? formatCurrency(item.multiBrandFare[1].totalPriceToDisplay, currency, currencyCulture)
          : ' '
        : item.multiBrandFare && item.multiBrandFare[0].totalPriceToDisplay
        ? formatCurrency(item.multiBrandFare[0].totalPriceToDisplay, currency, currencyCulture)
        : ' ',
    textDownValue: item.Duration || timeConvertObjectToDuration(item.duration),
    icon:
      Images.rail['24x24'][
        (item.vendorName || item.VendorName).toLowerCase().includes('acela') ? 'AC' : 'AM'
      ],
    textDownIcon: getSegmentsCountText(
      item.RailSegmentsCount ? item.RailSegmentsCount - 1 : item.railSegments.length - 1
    ),
    vendorName: item.VendorName || item.vendorName,
  };
};

const RailDetailItem = ({ item = {}, classFares = [] }) => {
  return (
    <View style={CommonStyles.mb3}>
      <RowView>
        <CardLeftTextDetail />
        <ShrinkView>
          <XSmallText>
            {(item.vendorName || item.VendorName) + ' #' + (item.trainNumber || item.TrainNumber)}
          </XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>D:</CardLeftTextDetail>
        <ShrinkView>
          <XSmallText>
            {timeConvert24toAMPM(item.departureTime || item.DepartTime) +
              ' (' +
              (item.departureLocationCode || item.DepartureLocationCode) +
              ') ' +
              (item.departureLocationName || item.DepartureLocationName)}
          </XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>A:</CardLeftTextDetail>
        <ShrinkView>
          <XSmallText>
            {timeConvert24toAMPM(item.arrivalTime || item.ArrivalTime) +
              ' (' +
              (item.arrivalLocationCode || item.ArrivalLocationCode) +
              ') ' +
              (item.arrivalLocationName || item.ArrivalLocationName)}
          </XSmallText>
          <XSmallText style={CommonStyles.mb2}>
            {(item.vendorName || item.VendorName || '').toLowerCase() +
              ' ' +
              (item.trainNumber || item.TrainNumber)}
          </XSmallText>
        </ShrinkView>
      </RowView>
      {classFares &&
        classFares.map((classFare) => (
          <RowView key={classFare.id}>
            <CardLeftTextDetail>C:</CardLeftTextDetail>
            <ShrinkView>
              <RowView style={[CommonStyles.mb2, CommonStyles.wrap]}>
                <XSmallText>{classFare.name + ' '}</XSmallText>
                <XSmallText>B: </XSmallText>
                <XSmallText>{classFare.classCode + ' | ' + classFare.segBrandNames}</XSmallText>
              </RowView>
            </ShrinkView>
          </RowView>
        ))}
    </View>
  );
};

const RailDetails = ({ item = {}, showClassFares, currency, currencyCulture }) => {
  const dispatch = useDispatch();
  const onBrandPressRailHandler = useCallback(
    (classFare) => {
      dispatch(selectRail(item, classFare));
    },
    [dispatch, item]
  );
  const classFares = showClassFares
    ? transformRailBrands(item.multiBrandFare, currency, currencyCulture)
    : [];

  return (
    <View style={cardStyle.composedRow}>
      <ShrinkView>
        {(item.RailSegments || item.railSegments) &&
          (item.RailSegments || item.railSegments).map((segment, index) => (
            <RailDetailItem
              key={index}
              item={segment}
              classFares={classFares?.length > 0 ? classFares : generateDefaultBrand(item)}
            />
          ))}
      </ShrinkView>
      {showClassFares && classFares && (
        <View>
          {classFares.map((classFare) => {
            const { id, price, name } = classFare;
            return (
              <PriceItem
                key={id}
                price={price}
                labelUp={name}
                onPress={() => onBrandPressRailHandler(classFare)}
              />
            );
          })}
        </View>
      )}
    </View>
  );
};

const RailCard = ({
  item = {},
  currentBrand,
  showClassFares,
  showVendorName,
  currency,
  currencyCulture,
  ...rest
}) => {
  const [data, setData] = useState(null);
  const [amenities, setAmenities] = useState([]);

  useEffect(() => {
    if (Object.keys(item).length > 0) {
      setData(transformRailDataToCard(item, currentBrand, currency, currencyCulture));

      const segments = item.railSegments || item.RailSegments;
      if (segments) {
        const am = segments.reduce((array, segment) => {
          const amens = segment.amenities || segment.Amenities;

          if (amens) {
            amens.forEach((am) => {
              if (!array.includes(am)) {
                if (am.code || am.Code) {
                  array.push((am.code || am.Code).toLowerCase());
                }
              }
            });
          }

          return array;
        }, []);

        setAmenities(am);
      }
    }
  }, [item, currentBrand]);

  return data ? (
    <Card
      title={data.title}
      subtitle={data.subtitle}
      value={data.value}
      textDownValue={data.textDownValue}
      textDownIcon={data.textDownIcon}
      icon={data.icon}
      uppercaseSubtitle
      textUpIcon={data.textUpIcon}
      childrenTitle={
        showVendorName &&
        data.vendorName && (
          <XSmallText style={[cardStyle.grayColor, CommonStyles.mt3]}>{data.vendorName}</XSmallText>
        )
      }
      detail={
        <RailDetails
          item={item}
          showClassFares={showClassFares}
          currency={currency}
          currencyCulture={currencyCulture}
        />
      }
      {...rest}
    >
      {Array.isArray(amenities) && (
        <RowView>
          {(amenities.includes('wifi') || amenities.includes('network')) && (
            <Icon style={styles.iconLittle} name="wifi" />
          )}
          {amenities.includes('baggage') && <Icon style={styles.iconLittle} name="bag" />}
          {amenities.includes('snack') && <Icon style={styles.iconLittle} name="snack" />}
          {amenities.includes('corporatefare') && (
            <Icon style={styles.iconLittle} color={Color.corporate} name="corporate" />
          )}
        </RowView>
      )}
    </Card>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  iconLittle: {
    color: Color.primary,
    fontSize: FontSize.small,
    ...CommonProperties.mr3,
  },
});

export default RailCard;
